import React, { RefObject } from 'react'
import styled from 'styled-components'
import iconSettingsGray100 from '~/assets/icons-light-theme/icon-settings-gray100.svg'
import { FontStyle, ImageButton, Text } from '~/components/core'
import { dimensions, light, spacing } from '~/constants/theme'
import { useUserClickedExternalElements } from '~/hooks/useUserClickedExternalElements'
import { desktop } from '~/styles'

import { MessageSnippet } from '@mangovoice/types'

import { backgroundBoxShadow } from './CommonStyles'

const SnippetsWrapper = styled.div`
  width: 100%;
  max-height: 224px;
  border-top-left-radius: ${dimensions.borderRadiusSM};
  border-top-right-radius: ${dimensions.borderRadiusSM};
  ${backgroundBoxShadow};
  position: absolute;
  bottom: 100%;
  background-color: ${light.gray100};
`

const SnippetsHeader = styled.div`
  width: 100%;
  padding: ${spacing.spacingSM} ${spacing.spacingLG};
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-top-left-radius: ${dimensions.borderRadiusSM};
  border-top-right-radius: ${dimensions.borderRadiusSM};
  background-color: ${light.dark800};
`

const SnippetListWrapper = styled.div`
  width: 100%;
  max-height: 180px;
  overflow: auto;
  border-left: 1px solid ${light.dark700};
  border-right: 1px solid ${light.dark700};
  scroll-behavior: smooth;

  &::-webkit-scrollbar {
    width: ${desktop.scrollbar}px;
    background-color: transparent;
  }

  &::-webkit-scrollbar-track {
    background-color: transparent;
  }

  &::-webkit-scrollbar-thumb {
    border-radius: ${desktop.scrollbar}px;
    -webkit-box-shadow: inset 0px 4px 14px rgba(0, 0, 0, 0.1);
    background-color: ${light.gray300};
  }
`

const SnippetItemWrapper = styled.div<{ active: boolean }>`
  width: 100%;
  padding: ${spacing.spacingLG};
  display: flex;
  align-items: center;
  border-top: 1px solid ${light.gray300};
  background-color: ${(props) =>
    props.active ? `${light.focus}` : light.gray100};
  cursor: pointer;
  &:focus {
    outline: none;
  }
  &:hover {
    background-color: ${light.focus};
  }
`

const TruncateChild = styled.div`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  padding-left: ${spacing.spacingMD};
`

interface SnippetPopupProps {
  data: Array<MessageSnippet>
  position: number
  onHover: (idx: number) => void
  onNavigate: () => void
  onSelect: (snippet: MessageSnippet) => void
  listRef: RefObject<HTMLDivElement>
  onShouldClose?: () => void
}
const SnippetPopup = ({
  data,
  position,
  onHover,
  onNavigate,
  onSelect,
  listRef,
  onShouldClose,
}: SnippetPopupProps) => {
  const handleMouseOver = (idx: number, e: React.MouseEvent) => {
    e.preventDefault()
    onHover(idx)
  }
  const wrapperRef = React.useRef<HTMLDivElement>(null)
  useUserClickedExternalElements([wrapperRef.current!], true, () => {
    onShouldClose?.()
  })

  return (
    <SnippetsWrapper ref={wrapperRef}>
      <SnippetsHeader>
        <Text
          fontStyle={FontStyle.Normal}
          text="Snippets"
          color={light.gray100}
        />
        <ImageButton
          icon={iconSettingsGray100}
          width="32px"
          height="32px"
          imgWidth={20}
          imgHeight={20}
          backColor="transparent"
          activeColor={light.dark800}
          hoveredColor={light.dark700}
          onClick={onNavigate}
        />
      </SnippetsHeader>
      <SnippetListWrapper ref={listRef}>
        {data.map((snippet, idx) => (
          <SnippetItemWrapper
            tabIndex={-1}
            key={`snippet-${snippet.id}`}
            active={position === idx}
            onClick={() => onSelect(snippet)}
            onMouseOver={(e) => handleMouseOver(idx, e)}
          >
            <Text
              fontStyle={FontStyle.bSmall}
              text={snippet.name}
              color={light.primary}
            />

            <div style={{ overflow: 'hidden' }}>
              <Text fontStyle={FontStyle.Tiny} color={light.dark800}>
                <TruncateChild>{snippet.body}</TruncateChild>
              </Text>
            </div>
          </SnippetItemWrapper>
        ))}
      </SnippetListWrapper>
    </SnippetsWrapper>
  )
}

export default SnippetPopup
